import service from '@/utils/https'

export interface ClientContactOption {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
  phone: string | null
  job_title: string | null
  client_id: string
  client_name: string
}

export interface ClientContact {
  id: string
  first_name: string | null
  last_name: string | null
  email: string
  phone: string | null
  job_title: string | null
}

export interface ClientLastMission {
  id: string
  name: string
}

export interface Client {
  id: string
  name: string
  siren_number: string | null
  address: string | null
  postal_code?: string | null
  city?: string | null
  country_code: string | null
  created_at: string
  updated_at: string
  team_client_contacts: ClientContact[]
  mission_count: number
  last_mission: ClientLastMission | null
}

export interface ClientDetail {
  id: string
  name: string
  siren_number: string | null
  address: string | null
  postal_code: string | null
  city: string | null
  country_code: string | null
  created_at: string
  updated_at: string
  created_by_id?: string | null
  created_by?: {
    id: string
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
  contacts: ClientContact[]
}

export interface CreateClientData {
  name: string
  siren_number?: string
  address?: string
  postal_code?: string
  city?: string
  country_code?: string
}

export interface UpdateClientData {
  name?: string
  siren_number?: string
  address?: string
  postal_code?: string
  city?: string
  country_code?: string
}

interface CreateClientContactData {
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  job_title?: string
}

interface UpdateClientContactData {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  job_title?: string
}

export interface GetClientsParams {
  search?: string
  page?: number
  pageSize?: number
  sortKey?: string
  sortDirection?: 'asc' | 'desc'
}

export interface GetClientsResult {
  items: Client[]
  total: number
}

export const clientService = {
  async getClients(paramsOrSearch?: GetClientsParams | string): Promise<GetClientsResult> {
    const params: GetClientsParams =
      typeof paramsOrSearch === 'string' ? { search: paramsOrSearch } : paramsOrSearch ?? {}
    const queryParams: Record<string, string> = {}
    if (params.search) queryParams.search = params.search
    if (params.page != null) queryParams.page = String(params.page)
    if (params.pageSize != null) queryParams.pageSize = String(params.pageSize)
    if (params.sortKey) queryParams.sortKey = params.sortKey
    if (params.sortDirection) queryParams.sortDirection = params.sortDirection
    const res = await service.get('/api/app/v1/clients', { params: queryParams })
    const data = res?.data ?? res
    if (data?.code === 200) {
      const d = data.data as { items?: Client[]; total?: number } | undefined
      return { items: d?.items ?? [], total: d?.total ?? 0 }
    }
    return { items: [], total: 0 }
  },

  async getClientContactOptions(search?: string) {
    const params: Record<string, string> = {}
    if (search) params.search = search
    const res = await service.get('/api/app/v1/client-contacts/options', { params })
    const data = res?.data ?? res
    if (data?.code === 200) {
      return (data.data?.items || []) as ClientContactOption[]
    }
    return [] as ClientContactOption[]
  },

  async createClient(data: CreateClientData) {
    const res = await service.post('/api/app/v1/clients', data)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data as { id: string; name: string }
    }
    throw new Error(responseData?.message || 'Failed to create client')
  },

  async updateClient(clientId: string, data: UpdateClientData) {
    const res = await service.put(`/api/app/v1/clients/${clientId}`, data)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data
    }
    throw new Error(responseData?.message || 'Failed to update client')
  },

  async deleteClient(clientId: string) {
    const res = await service.delete(`/api/app/v1/clients/${clientId}`)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return true
    }
    throw new Error(responseData?.message || 'Failed to delete client')
  },

  async createClientContact(clientId: string, data: CreateClientContactData) {
    const res = await service.post(`/api/app/v1/clients/${clientId}/contacts`, data)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data as { id: string; first_name: string; last_name: string; email: string }
    }
    throw new Error(responseData?.message || 'Failed to create contact')
  },

  async updateClientContact(clientId: string, contactId: string, data: UpdateClientContactData) {
    const res = await service.put(`/api/app/v1/clients/${clientId}/contacts/${contactId}`, data)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return responseData.data
    }
    throw new Error(responseData?.message || 'Failed to update contact')
  },

  async deleteClientContact(clientId: string, contactId: string) {
    const res = await service.delete(`/api/app/v1/clients/${clientId}/contacts/${contactId}`)
    const responseData = res?.data ?? res
    if (responseData?.code === 200) {
      return true
    }
    throw new Error(responseData?.message || 'Failed to delete contact')
  },

  async getClient(clientId: string) {
    const res = await service.get(`/api/app/v1/clients/${clientId}`)
    const data = res?.data ?? res
    if (data?.code === 200) {
      return data.data as ClientDetail
    }
    throw new Error((data as { message?: string })?.message || 'Failed to fetch client')
  },

  async getClientMissions(clientId: string) {
    const res = await service.get(`/api/app/v1/clients/${clientId}/missions`)
    const data = res?.data ?? res
    if (data?.code === 200) {
      const payload = data.data as ClientMissionsResult | undefined
      return {
        items: payload?.items ?? [],
        metrics: payload?.metrics ?? { mission_count: 0, total_revenue_excl_vat: 0, can_delete: false },
      }
    }
    return {
      items: [] as ClientMission[],
      metrics: { mission_count: 0, total_revenue_excl_vat: 0, can_delete: false },
    }
  },

  async getClientShareLink(clientId: string) {
    const res = await service.get(`/api/app/v1/clients/${clientId}/share-link`)
    const data = res?.data ?? res
    if (data?.code === 200) {
      return data.data as {
        has_active_link: boolean
        url: string | null
        created_at: string | null
      }
    }
    throw new Error((data as { message?: string })?.message || 'Failed to fetch share link')
  },

  async createClientShareLink(clientId: string) {
    const res = await service.post(`/api/app/v1/clients/${clientId}/share-link`)
    const data = res?.data ?? res
    if (data?.code === 200) {
      return data.data as {
        url: string | null
        has_active_link: boolean
        created_at: string | null
      }
    }
    throw new Error((data as { message?: string })?.message || 'Failed to create share link')
  },

  async importClientsCsv(clients: Array<{
    name: string
    siren_number?: string
    address?: string
    country_code?: string
    contact_1_first_name?: string
    contact_1_last_name?: string
    contact_1_email?: string
    contact_1_phone?: string
    contact_1_job_title?: string
    contact_2_first_name?: string
    contact_2_last_name?: string
    contact_2_email?: string
    contact_2_phone?: string
    contact_2_job_title?: string
    contact_3_first_name?: string
    contact_3_last_name?: string
    contact_3_email?: string
    contact_3_phone?: string
    contact_3_job_title?: string
  }>) {
    const res = await service.post('/api/app/v1/clients/import-csv', { clients })
    const data = res?.data ?? res
    if (data?.code === 200) {
      return data.data as {
        results: Array<{ name: string; status: string; message: string }>
        total: number
        created: number
        skipped: number
        errors: number
      }
    }
    throw new Error((data as { message?: string })?.message || 'Failed to import clients')
  },
}

export interface ClientMissionFreelancer {
  id: string
  first_name: string | null
  last_name: string | null
  avatar: string | null
}

export interface ClientMission {
  id: string
  name: string
  project_code: string
  status: string
  start_date: string | null
  end_date: string | null
  freelancer_name: string
  freelancer: ClientMissionFreelancer | null
  contact_name: string
  client_rate_amount?: number | null
}

export interface ClientMissionsMetrics {
  mission_count: number
  total_revenue_excl_vat: number
  can_delete?: boolean
}

export interface ClientMissionsResult {
  items: ClientMission[]
  metrics: ClientMissionsMetrics
}
